'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, Compass, Map, BookOpen, Layers, Building2, ChevronLeft, ChevronRight,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  group?: string;
  badge?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-home', label: 'Home', href: '/', icon: Home, group: 'primary' },
  { id: 'nav-navigator', label: 'Navigator', href: '/navigator', icon: Compass, group: 'primary' },
  { id: 'nav-atlas', label: 'Atlas', href: '/atlas', icon: Map, group: 'ecosystem' },
  { id: 'nav-codex', label: 'Codex', href: '/codex', icon: BookOpen, group: 'ecosystem' },
  { id: 'nav-product-framework', label: 'Product Framework', href: '/product-framework', icon: Layers, group: 'product' },
  { id: 'nav-pf001', label: 'PF-001 Enterprise', href: '/pf-001-enterprise', icon: Building2, group: 'product', badge: 'Draft' },
];

const navGroups: Record<string, string> = {
  primary: 'Navigation',
  ecosystem: 'Ecosystem',
  product: 'Product',
};

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const pathname = usePathname();

  const groupedItems = navItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const group = item.group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  return (
    <aside
      className={`sidebar-transition flex-shrink-0 flex flex-col bg-card border-r border-border relative z-20 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ minHeight: '100vh' }}
    >
      {/* Logo area */}
      <div className={`flex items-center border-b border-border ${collapsed ? 'justify-center px-0 h-14' : 'px-5 h-14 gap-3'}`}>
        {/* K-compass wordmark */}
        <div className="flex-shrink-0 w-8 h-8 relative">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <circle cx="16" cy="16" r="14" stroke="rgba(79,142,247,0.2)" strokeWidth="1"/>
            <circle cx="16" cy="16" r="10" stroke="rgba(79,142,247,0.1)" strokeWidth="1"/>
            {/* Compass points */}
            <path d="M16 4 L16 8" stroke="#4f8ef7" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M16 24 L16 28" stroke="#4f8ef7" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M4 16 L8 16" stroke="#4f8ef7" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M24 16 L28 16" stroke="#4f8ef7" strokeWidth="1.5" strokeLinecap="round"/>
            {/* K form */}
            <path d="M12 11 L12 21" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16 L20 11" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16 L20 21" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="16" cy="16" r="2" fill="rgba(79,142,247,0.4)"/>
          </svg>
        </div>
        {!collapsed && (
          <div className="flex flex-col">
            <span className="text-sm font-semibold tracking-[0.12em] text-foreground uppercase">KAVRIS</span>
            <span className="text-[10px] font-mono-data text-muted-foreground tracking-widest">Navigate Intelligence</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {Object.entries(groupedItems).map(([group, items]) => (
          <div key={`group-${group}`} className="mb-4">
            {!collapsed && (
              <span className="px-2 mb-1 block text-[10px] font-medium tracking-[0.12em] uppercase text-muted-foreground">
                {navGroups[group] || group}
              </span>
            )}
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`group flex items-center rounded transition-all duration-150 mb-0.5 ${
                    collapsed ? 'justify-center w-10 h-10 mx-auto' : 'gap-3 px-3 py-2'
                  } ${
                    isActive
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon size={16} className={`flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium flex-1 truncate">{item.label}</span>
                      {item.badge && (
                        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-status-draft status-draft">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Prototype label + collapse toggle */}
      <div className="border-t border-border p-2">
        {!collapsed && (
          <div className="px-2 py-1.5 mb-2 rounded bg-muted/30 border border-border">
            <span className="prototype-badge text-muted-foreground">Experimental Prototype</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center h-8 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>
    </aside>
  );
}